import React, { useEffect, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import { Tabs } from "antd";
import { Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/lib/table/interface";

import { Page } from "../../components/page";
import { PageHeader } from "../../components/page_header";
import { DateRange, MarketSelector, Selector } from "../../components/selectors";
import { ResolutionSelector } from "../../components/selectors/resolution_selector";
import { Platform, ReportFormat, ResolutionType } from "../../store/common_types";
import {
  hostsChartFetchRequest,
  hostsFetchRequest,
  hostsSummaryFetchRequest,
  reportGeneratorTaskCreateRequest,
} from "../../store/hosts/actions";
import { HostItem, HostsMetric } from "../../store/hosts/types";
import { RootState } from "../../store/root_reducer";
import { enumParams } from "../../utils/enum_params";
import { MomentParam } from "../../utils/moment_param";
import { rangeResolutionLimit, resolutionUnitMap } from "../../utils/resolution";

import { DataTable, HostsChart } from "./components";
import styles from "./styles.module.scss";

const availableMetrics: HostsMetric[] = ["searches", "clicks", "ctr", "pbc", "str"];
const availablePlatforms: Platform[] = ["web", "android", "iphone", "ipad", "mobile", "other"];

export const HostsPage: React.FC = () => {
  const { t } = useTranslation(["hosts_page", "common"]);

  const hostsState = useSelector((state: RootState) => state.Hosts);
  const markets = useSelector((state: RootState) => state.Markets.markets);
  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);

  const [refresh, setRefresh] = useState(true);

  const [commonState, setCommonState] = useQueryParams({
    from: withDefault(MomentParam, moment.utc().startOf("day").subtract(1, "week").add(1, "day")),
    to: withDefault(MomentParam, moment.utc().startOf("day")),
    market: withDefault(StringParam, undefined),
    resolution: withDefault(
      enumParams<ResolutionType>(StringParam, ["1d", "1m"]),
      "1d"
    ),
  });
  const [tableState, setTableState] = useQueryParams({
    pageSize: withDefault(NumberParam, 10),
    page: withDefault(NumberParam, 1),
    platform: withDefault(enumParams<Platform>(StringParam, availablePlatforms), "web"),
    ordering: withDefault(StringParam, "-date"),
  });
  const [chartState, setChartState] = useQueryParams({
    metric: withDefault(enumParams<HostsMetric>(StringParam, availableMetrics), "searches"),
  });

  useEffect(() => {
    setCommonState((state) => state, "replaceIn");
    setTableState((state) => state, "replaceIn");
    setChartState((state) => state, "replaceIn");
  }, [setChartState, setCommonState, setTableState]);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      hostsFetchRequest({
        ...commonState,
        ...tableState,
        platform: [tableState.platform] as Platform[],
        gateId: selectedGate.id,
      })
    );
  }, [dispatch, refresh, commonState, tableState, selectedGate]);
  useEffect(() => {
    dispatch(hostsChartFetchRequest({ ...commonState, ...chartState, gateId: selectedGate.id }));
  }, [dispatch, refresh, commonState, chartState, selectedGate]);
  useEffect(() => {
    dispatch(hostsSummaryFetchRequest({ ...commonState, platform: [tableState.platform], gateId: selectedGate.id }));
  }, [dispatch, refresh, commonState, tableState.platform, selectedGate]);

  const handleSelectDatesRange = (dateRange: DateRange) => {
    const rangeDiffDays = dateRange.to.diff(dateRange.from, "days");
    setCommonState((current) => ({
      ...current,
      ...dateRange,
      resolution: rangeResolutionLimit(rangeDiffDays, current.resolution),
    }));
    setTableState((current) => ({ ...current, page: 1 }), "replaceIn");
  };
  const handleMarketSelect = (market?: string) => {
    setCommonState((currentPayload) => ({ ...currentPayload, market }));
    setTableState((current) => ({ ...current, page: 1 }), "replaceIn");
  };

  const handleMetricSelect = (metric?: string) => {
    setChartState((current) => ({ ...current, metric: metric as HostsMetric }));
  };

  const handleReport = (reportFormat: ReportFormat) => {
    dispatch(
      reportGeneratorTaskCreateRequest({
        ...commonState,
        ...tableState,
        reportFormat,
        gateId: selectedGate.id,
      })
    );
  };
  const handleReload = () => {
    setRefresh((state) => !state);
  };

  const handleChangeTab = (platform: string) => {
    setTableState((current) => ({ ...current, platform: platform as Platform, page: 1 }));
  };
  const handleTableChange = (
    { current, pageSize }: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<HostItem> | SorterResult<HostItem>[],
    { action }: TableCurrentDataSource<HostItem>
  ) => {
    switch (action) {
      case "paginate": {
        if (current && pageSize)
          setTableState((currentState) => ({
            ...currentState,
            page: currentState.pageSize !== pageSize ? 1 : current,
            pageSize,
          }));
        break;
      }
      case "sort":
        if (!Array.isArray(sorter)) {
          const { field, order } = sorter;
          const ordering = `${order === "descend" ? "-" : ""}${field}`;
          setTableState((currentState) => ({ ...currentState, ordering }));
        }
        break;
      default:
        break;
    }
  };

  return (
    <Page title={t("page.title")} description={t("page.description")} reportTTL={365} report="hosts">
      <PageHeader
        onReload={handleReload}
        onDownloadReport={handleReport}
        onDateRangeSelect={handleSelectDatesRange}
        reportDownloadDisabled={hostsState.count === 0}
        dateRange={{ from: commonState.from, to: commonState.to }}
        isLoading={hostsState.dataIsLoading || hostsState.chartDataIsLoading}
      >
        <MarketSelector markets={markets} value={commonState.market} onSelect={handleMarketSelect} />
        <Selector
          title={t("page.metrics-selector.title")}
          value={chartState.metric}
          options={availableMetrics.map((metric) => ({
            value: metric,
            title: t(`page.metrics-selector.values.${metric}`),
          }))}
          onSelect={handleMetricSelect}
        />
        <ResolutionSelector
          resolution={commonState.resolution}
          options={["1d", "1m"]}
          onSelect={(resolution) => {
            setCommonState((state) => ({ ...state, resolution }));
            setTableState((state) => ({ ...state, page: 1 }));
          }}
        />
      </PageHeader>
      <HostsChart
        rangeFrom={moment(commonState.from)}
        rangeTo={moment(commonState.to)}
        items={hostsState.chartItems}
        loading={hostsState.chartDataIsLoading}
        resolution={commonState.resolution}
        metric={chartState.metric as HostsMetric}
      />
      <Tabs className={styles.tabs} activeKey={tableState.platform} onChange={handleChangeTab}>
        {availablePlatforms.map((platform) => (
          <Tabs.TabPane tab={t(`common:platforms.${platform}`)} key={platform} />
        ))}
      </Tabs>
      <DataTable
        timestampFormat={resolutionUnitMap[commonState.resolution].tooltipFormat as string}
        loading={hostsState.dataIsLoading || hostsState.summaryIsLoading}
        total={hostsState.count}
        dataSource={hostsState.items}
        currentPage={tableState.page}
        pageSize={tableState.pageSize}
        onChange={handleTableChange}
        ordering={tableState.ordering}
        summary={hostsState.summary}
      />
    </Page>
  );
};
